import { useCallback, useEffect, useRef, useState } from "react";
import { Search, RefreshCw, ArrowDownLeft, ArrowUpRight, X, Bell, BellOff } from "lucide-react";
import { api, OPERATORS, fmtTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimSidebar } from "@/components/SimSidebar";
import { CopyTopup } from "@/components/CopyTopup";
import { ExportSms } from "@/components/ExportSms";
import { useAuth } from "@/context/Auth";
import { PtBadge, colorMap } from "@/components/PtBadge";
import { beep, desktopNotify, askPermission } from "@/lib/notify";
import { toast } from "sonner";

const ALL = "__all";

function Filters({ f, setF, devices }) {
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v === ALL ? "" : v }));
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
      <div className="relative col-span-2">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
        <Input data-testid="sms-search-input" placeholder="Cari isi pesan…" value={f.q} onChange={(e) => set("q")(e.target.value)} className="pl-9 h-9 bg-white" />
      </div>
      <Input data-testid="sms-sender-input" placeholder="Pengirim" value={f.sender} onChange={(e) => set("sender")(e.target.value)} className="h-9 bg-white" />
      <Select value={f.device_id || ALL} onValueChange={set("device_id")}>
        <SelectTrigger data-testid="sms-device-filter" className="h-9 bg-white"><SelectValue placeholder="Device" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>Semua device</SelectItem>{devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
      </Select>
      <div className="flex gap-1 col-span-2 md:col-span-3 xl:col-span-2">
        <Input data-testid="sms-date-from" type="date" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9 bg-white px-2 text-xs" />
        <Input data-testid="sms-date-to" type="date" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9 bg-white px-2 text-xs" />
      </div>
    </div>
  );
}

function SmsRow({ m, i, colors }) {
  const op = OPERATORS[m.operator];
  return (
    <div data-testid={`sms-row-${m.id}`} className={`rise grid md:grid-cols-[150px_36px_150px_1fr_110px] gap-x-4 gap-y-1 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${!m.read ? "bg-emerald-500/[0.04]" : ""}`} style={{ animationDelay: `${Math.min(i, 15) * 20}ms` }}>
      <div className="flex items-center gap-2 min-w-0">
        {!m.read && <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />}
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="font-bold text-emerald-700 truncate">{m.sim_name || <span className="text-slate-500 font-normal italic">tanpa nama</span>}</span>
            <PtBadge name={m.company} colors={colors} />
          </span>
          <span className="block text-[11px] font-mono text-slate-500">{m.device_name} · {m.port}.{m.slot} {op && <span className={op.color}>{op.short}</span>}</span>
        </span>
      </div>
      <CopyTopup m={m} />
      <div className="flex items-center gap-1.5 text-sm min-w-0">
        {m.direction === "out" ? <ArrowUpRight className="h-3.5 w-3.5 text-sky-600 shrink-0" /> : <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-700 shrink-0" />}
        <span className="truncate font-medium">{m.direction === "out" ? `ke ${m.receiver}` : m.sender}</span>
      </div>
      <div className="text-sm text-slate-700 break-words whitespace-pre-wrap">{m.text}{m.direction === "out" && <span className="ml-2 text-[11px] text-sky-600">[{m.status}]</span>}</div>
      <div className="text-xs text-slate-500 font-mono md:text-right">{fmtTime(m.received_at)}</div>
    </div>
  );
}

export default function Inbox() {
  const [names, setNames] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [company, setCompany] = useState("");
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState({ q: "", sender: "", device_id: "", operator: "", date_from: "", date_to: "" });
  const [data, setData] = useState({ total: 0, items: [] });
  const { can } = useAuth();
  const [limit, setLimit] = useState(50);
  const selKey = selected.join(",");
  const reqId = useRef(0);
  const [loading, setLoading] = useState(false);
  const [notifyOn, setNotifyOn] = useState(() => localStorage.getItem("sms_notify") !== "off");
  const seen = useRef(null);
  const [onlyOn, setOnlyOn] = useState(() => localStorage.getItem("sms_only_on") !== "off");
  const toggleOn = () => setOnlyOn((v) => { localStorage.setItem("sms_only_on", v ? "off" : "on"); return !v; });
  const recent = !f.date_from && !f.date_to;

  const buildParams = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    if (recent) params.hours = 24;
    if (selKey) params.keys = selKey;
    else {
      if (company) params.company = company;
      if (onlyOn) params.on = true;
    }
    return params;
  }, [f, selKey, company, onlyOn, recent]);

  const load = useCallback(async (silent) => {
    const params = { ...buildParams(), limit };
    const id = ++reqId.current;
    if (silent !== true) setLoading(true);
    let s;
    try {
      s = await api.get("/sms", { params });
    } catch {
      if (id === reqId.current) setLoading(false);
      return;
    }
    if (id !== reqId.current) return;
    setLoading(false);
    const incoming = s.data.items.filter((m) => m.direction === "in");
    if (silent === true && seen.current && notifyOn) {
      const fresh = incoming.filter((m) => !seen.current.has(m.id) && m.received_at > seen.current.newest);
      if (fresh.length) {
        beep();
        const m = fresh[0];
        const who = m.sim_name ? `${m.sim_name}${m.company ? ` ${m.company}` : ""}` : `${m.device_name} ${m.port}.${m.slot}`;
        toast(`SMS baru · ${who}${fresh.length > 1 ? ` (+${fresh.length - 1})` : ""}`, { description: `${m.sender}: ${m.text.slice(0, 120)}` });
        desktopNotify(`SMS baru · ${who}`, `${m.sender}: ${m.text.slice(0, 120)}`);
      }
    }
    const ids = new Set(incoming.map((m) => m.id));
    ids.newest = incoming.reduce((a, m) => (m.received_at > a ? m.received_at : a), seen.current?.newest || "");
    seen.current = ids;
    setData(s.data);
  }, [buildParams, limit, notifyOn]);

  const loadNames = useCallback(() => {
    api.get("/sim-names").then((r) => setNames(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadNames();
    const t = setInterval(loadNames, 15000);
    return () => clearInterval(t);
  }, [loadNames]);

  useEffect(() => { api.get("/devices").then((r) => setDevices(r.data)); }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 10000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    if (selKey) api.post("/sms/mark-read", { keys: selKey.split(",") }).then(() => setTimeout(() => api.get("/sim-names").then((r) => setNames(r.data)), 3000));
  }, [selKey]);
  useEffect(() => { setSelected([]); }, [company, f.operator]);

  const ops = f.operator ? f.operator.split(",") : [];
  const setOps = (v) => setF((o) => ({ ...o, operator: v.join(",") }));
  const opTitle = ops.map((k) => OPERATORS[k]?.short).join("+");
  const label = (k) => { const n = names.find((x) => x.key === k); return n ? `${n.name}${n.company ? ` ${n.company}` : ""}` : "?"; };
  const title = selected.length ? selected.slice(0, 4).map(label).join(", ") + (selected.length > 4 ? ` +${selected.length - 4}` : "") : [company, opTitle, onlyOn && "SIM ON"].filter(Boolean).join(" · ");
  const clear = () => { setSelected([]); setCompany(""); setOps([]); setSearch(""); };

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <SimSidebar names={names} setNames={setNames} selected={selected} setSelected={setSelected}
        company={company} setCompany={setCompany} companies={companies} ops={ops} setOps={setOps} search={search} setSearch={setSearch} onlyOn={onlyOn} toggleOn={toggleOn} />
      <section className="flex-1 min-w-0 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Kotak SMS</p>
            <h1 data-testid="inbox-title" className="font-display text-4xl font-bold leading-tight break-words">{title ? <span className="text-emerald-700">{title}</span> : "Semua SIM"}</h1>
          </div>
          <span data-testid="sms-total" className="text-sm text-slate-500 font-mono mb-1.5">{data.total} pesan{recent ? " · 24 jam terakhir" : ""}</span>
          {(selected.length || company || ops.length || search) && <Button data-testid="clear-name-filter" variant="ghost" size="sm" onClick={clear}><X className="h-4 w-4 mr-1" />Hapus filter</Button>}
          <span className="ml-auto" />
          {can("export_sms") && <ExportSms getParams={buildParams} total={data.total} />}
          <Button data-testid="sms-notify-toggle" variant="outline" size="sm"
            title="Bunyi & pop-up saat SMS baru masuk untuk filter yang sedang dipakai"
            onClick={() => { const v = !notifyOn; setNotifyOn(v); localStorage.setItem("sms_notify", v ? "on" : "off"); if (v) { askPermission(); beep(); } }}>
            {notifyOn ? <Bell className="h-4 w-4 mr-1 text-emerald-700" /> : <BellOff className="h-4 w-4 mr-1" />}{notifyOn ? "Notifikasi ON" : "Notifikasi OFF"}
          </Button>
          <Button data-testid="sms-refresh-button" variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Muat ulang</Button>
        </div>
        <Filters f={f} setF={setF} devices={devices} />
        <div className={`rounded-lg border border-slate-200 bg-white overflow-hidden transition-opacity duration-150 ${loading ? "opacity-50" : ""}`} data-testid="sms-list">
          <div className="hidden md:grid grid-cols-[150px_36px_150px_1fr_110px] gap-x-4 gap-y-1 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <span>Nama SIM / slot</span><span>Salin</span><span>Pengirim</span><span>Isi pesan</span><span className="text-right">Waktu</span>
          </div>
          {data.items.map((m, i) => <SmsRow key={m.id} m={m} i={i} colors={colorMap(companies)} />)}
          {!data.items.length && <div className="p-10 text-center text-slate-500 text-sm">{recent ? "Tidak ada SMS dalam 24 jam terakhir. Pakai filter tanggal untuk melihat SMS lama." : "Tidak ada SMS untuk filter ini."}</div>}
          {data.items.length < data.total && (
            <div className="p-3 text-center"><Button data-testid="sms-load-more" variant="ghost" size="sm" onClick={() => setLimit((l) => l + 50)}>Tampilkan lebih banyak</Button></div>
          )}
        </div>
      </section>
    </div>
  );
}
